import { getFlagVariationDisplayValue } from '@gonfalon/flag-variations/getFlagVariationDisplayValue';
import { fromJS, Record } from '@gonfalon/immutable-shim/immutableShim';
import { validateRecord } from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import { couldBeNumber } from '@gonfalon/types/couldBeNumber';
import { stringifyValue, type StringifyValueOptions } from '@gonfalon/types/stringifyValue';
import { isBoolean, isEmpty } from 'lodash';
import { v4 } from 'uuid';

import type { VariationType } from '#utils/flags/flagConstants';
import type { VariationValue } from '#utils/flags/flagTypes';
import { matchesType, valueExists } from '#utils/flags/validation';

export type VariationProps = {
  _id: string;
  _key: string;
  _isDraft?: boolean;
  value: VariationValue;
  name: string;
  description: string;
  originalAIValue?: string;
  color?: string;
  valueHash?: string;
};

export class Variation extends Record<VariationProps>({
  _id: '',
  _key: '',
  value: '',
  name: '',
  description: '',
  originalAIValue: undefined,
  color: undefined,
  valueHash: undefined,
}) {
  validate({ requiredType }: { requiredType?: VariationType } = {}) {
    let checks = [valueExists('value')];

    if (requiredType !== null && requiredType !== undefined) {
      checks = checks.concat(matchesType(requiredType)('value'));
    }

    return validateRecord(this, ...checks);
  }

  toRep() {
    return fromJS(this.toJSON()).withMutations((map: Variation) => {
      map.remove('_key');
      map.remove('_id');
    });
  }

  isEmpty() {
    return !isBoolean(this.value) && !couldBeNumber(this.value) && isEmpty(this.value);
  }

  // JSON variations get turned into Immutable.Collection upon response handling.
  // Without this step, a JSON object variation would render as Map { ... }.
  getValueString(options?: StringifyValueOptions) {
    return stringifyValue(this.toJS().value, options);
  }

  getDisplay(options?: StringifyValueOptions) {
    return getFlagVariationDisplayValue(this.toJS(), options);
  }
}

export function createVariation(props: CreateFunctionInput<Variation> = {}) {
  const variation = props instanceof Variation ? props : new Variation(fromJS(props));
  return variation
    .update('_key', (key) => (key.length === 0 ? v4() : key))
    .update('_id', (id) => (id.length === 0 ? v4() : id));
}
